import { useRef } from 'react';
import { css } from '@emotion/react';
import dayjs from 'dayjs';
import { Trans } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { theme } from '@frontend/theme';
import { Button, currency, SpinningLoader, Text, useTooltip } from '@frontend/design-system';
import { useCareCreditTranslations } from '../hooks/use-care-credit-translations';
import { CareCreditPatientData } from '../types/care-credit.types';
import { CareCreditChip } from './care-credit-chip';

const styles = {
  item: css`
    position: relative;
    padding: var(--fabric-space-padding-md);
    border-radius: var(--fabric-radius-md);
    background: var(--fabric-color-neutral-00);
    box-shadow: ${theme.shadows.light};
  `,
  topRow: css`
    display: flex;
    gap: var(--fabric-space-gap-md);
    align-items: center;
    justify-content: space-between;
    margin-bottom: var(--fabric-space-gap-xs);

    .top-row__brand-line {
      flex-grow: 0;
    }
    .top-row__chip {
      flex-grow: 1;
      display: flex;
      justify-content: flex-end;
    }
  `,
  brandLine: css`
    display: flex;
    align-items: center;
    line-height: 1;
    gap: var(--fabric-space-gap-xs);
    font-size: var(--fabric-font-size-lg);
  `,
  loadingContainer: css`
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(255, 255, 255, 0.5);
  `,
  spinner: css`
    opacity: 0.5;
  `,
  subText: css`
    color: var(--fabric-color-neutral-50);
    font-weight: 400;
  `,
  unknownStatusBox: css`
    background: var(--fabric-color-neutral-5);
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    padding: var(--fabric-space-padding-md);
    border-radius: var(--fabric-radius-md);
    gap: var(--fabric-space-gap-xs);
    color: var(--fabric-color-neutral-70);

    svg {
      color: var(--fabric-color-neutral-60);
    }
  `,
  bottomRowJsx: css`
    display: flex;
    flex-direction: column;
    gap: var(--fabric-space-gap-xs);
    margin-left: var(--fabric-space-padding-lg);
  `,
};

export type CareCreditPersonStatusItemProps = {
  className?: string;
  careCreditData?: CareCreditPatientData;
  isLoading?: boolean;
  hasValidPhoneNumber?: boolean;
  onClickPrequalify?: () => void;
  onClickPreApproved?: () => void;
  onClickCardholder?: () => void;
};
export const CareCreditPersonStatusItem = ({
  careCreditData,
  className,
  isLoading,
  hasValidPhoneNumber,
  onClickPrequalify,
  onClickPreApproved,
  onClickCardholder,
}: CareCreditPersonStatusItemProps) => {
  const { t } = useCareCreditTranslations();

  const { status, amount, asOfDate } = careCreditData || {};

  let bottomRowJsx: React.ReactNode = null;
  let chipJsx: React.ReactNode = null;

  const { Tooltip, triggerProps, tooltipProps } = useTooltip({
    placement: 'left',
  });

  const chipContainerRef = useRef<HTMLDivElement>(null);

  const formattedAmount = amount ? currency(amount, { decimals: 0 }) : 'N/A';
  const date = asOfDate ? dayjs(asOfDate).format('MMM DD, YYYY') : null;
  const time = asOfDate ? dayjs(asOfDate).format('h:mm A') : null;

  if (!isLoading) {
    if (!careCreditData) {
      bottomRowJsx = (
        <div css={styles.unknownStatusBox}>
          <div>
            <Icon name='help' size={16} />
          </div>
          <div>
            <Text size='medium'>{t('Unable to retrieve CareCredit eligibility status at this time')}</Text>
          </div>
        </div>
      );
    } else if (status === 'approved') {
      bottomRowJsx = (
        <div css={styles.bottomRowJsx}>
          <div>
            <Text size='medium'>
              <Trans translate={t}>
                <b>{formattedAmount}</b> available
                <br />
              </Trans>
            </Text>
            <Text size='small' color='subdued'>
              {t('As of {{date}} at {{time}}', { date, time })}
            </Text>
          </div>
          <div>
            <Button variant='secondary' onClick={onClickCardholder} disabled={!onClickCardholder}>
              {t('Take Payment')}
            </Button>
          </div>
        </div>
      );
    } else if (status === 'pre-approved') {
      bottomRowJsx = (
        <div css={styles.bottomRowJsx}>
          <div>
            {hasValidPhoneNumber === false ? (
              <>
                <Button variant='secondary' {...triggerProps} disabled={true}>
                  {t('Send Approval Link')}
                </Button>
                <Tooltip {...tooltipProps}>
                  {t('Customer does not have a phone number that can receive text messages.')}
                </Tooltip>
              </>
            ) : (
              <Button variant='secondary' onClick={onClickPreApproved} disabled={!onClickPreApproved}>
                {t('Send Approval Link')}
              </Button>
            )}
          </div>
          <div>
            <Text size='small'>
              <i css={styles.subText}>{t('Customer can accept offer and pay after.')}</i>
            </Text>
          </div>
        </div>
      );
    } else if (status === 'not-enrolled') {
      bottomRowJsx = (
        <div css={styles.bottomRowJsx}>
          <div>
            <Button variant='secondary' onClick={onClickPrequalify} disabled={!onClickPrequalify}>
              {t('Check Now')}
            </Button>
          </div>
          <div>
            <Text size='small'>
              <i css={styles.subText}>{t('Customer can complete approval and pay after.')}</i>
            </Text>
          </div>
        </div>
      );
    }

    if (status) {
      chipJsx = <CareCreditChip status={status} containerRef={chipContainerRef} isResponsive />;
    }
  }

  return (
    <div className={`care-credit-person-status-item ${className || ''}`} css={styles.item}>
      <div className='top-row' css={styles.topRow}>
        <div className='top-row__brand-line'>
          <Text size='medium' weight='bold' css={styles.brandLine}>
            <Icon name='care-credit' height={16} width={16} />
            {t('CareCredit')}
          </Text>
        </div>
        <div className='top-row__chip' ref={chipContainerRef}>
          {chipJsx}
        </div>
      </div>

      <div className='bottom-row'>{bottomRowJsx}</div>

      {isLoading && (
        <div css={styles.loadingContainer}>
          <SpinningLoader size='xs' css={styles.spinner} />
        </div>
      )}
    </div>
  );
};
